// 偵測區域繪製 UI (APP#45) — 在預覽框上「拖矩形」框出 include ROI，或選常用 preset，
// 存 → DetectionZones raw op:set (rawVal region JSON，見 zones.ts)。
// ⚠️ web snapshot API 未落地 → 預覽框以比例底圖 placeholder 呈現；區域座標本就解析度無關 (0–100%)，
// 未來 snapshot 落地可直接當背景。讀回若空 / 韌體 bug "0" → 優雅退回 (顯示未設定、預填全畫面、不 crash)。
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { signaling } from '../../../lib/ws';
import { MOTION_DETECTION_ZONES } from './ids';
import { opGet } from './ops';
import {
  clampRegion,
  encodeZones,
  FULL_FRAME,
  parseZoneReport,
  rectFromDrag,
  ZONE_PRESETS,
  type ZoneRegion,
} from './zones';

type LoadState = 'loading' | 'set' | 'unset' | 'unavailable';

export function ZoneEditor({ deviceId, disabled }: { deviceId: string; disabled?: boolean }) {
  const { t } = useTranslation();
  const [region, setRegion] = useState<ZoneRegion>(FULL_FRAME);
  const [load, setLoad] = useState<LoadState>('loading');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef<{ x: number; y: number } | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // 讀回目前區域 (op:get)。空 / "0" / 非法 → unset (退回全畫面繪製底)；讀取失敗 → unavailable。
  useEffect(() => {
    let cancelled = false;
    opGet(deviceId, MOTION_DETECTION_ZONES).then(
      (f) => {
        if (cancelled || !alive.current) return;
        const parsed = parseZoneReport(f);
        if (parsed) {
          setRegion(parsed);
          setLoad('set');
        } else {
          setLoad('unset');
        }
      },
      () => {
        if (!cancelled && alive.current) setLoad('unavailable');
      }
    );
    return () => {
      cancelled = true;
    };
  }, [deviceId]);

  const posFromEvent = useCallback((e: React.PointerEvent) => {
    const box = surfaceRef.current;
    if (!box) return null;
    const rect = box.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    return {
      x: Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)),
    };
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (disabled) return;
      const p = posFromEvent(e);
      if (!p) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      dragStart.current = p;
      setSaved(false);
      setRegion(rectFromDrag(p.x, p.y, p.x, p.y));
    },
    [disabled, posFromEvent]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (disabled || !dragStart.current) return;
      const p = posFromEvent(e);
      if (!p) return;
      const s = dragStart.current;
      setRegion(rectFromDrag(s.x, s.y, p.x, p.y));
    },
    [disabled, posFromEvent]
  );

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    if (!dragStart.current) return;
    dragStart.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  async function save() {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      await signaling.sendCmd(deviceId, 'set', MOTION_DETECTION_ZONES, { rawVal: encodeZones(region) });
      if (!alive.current) return;
      setLoad('set');
      setSaved(true);
    } catch (err) {
      if (!alive.current) return;
      setSaveError(err instanceof Error ? err.message : t('deviceSettings.zoneSaveFailed'));
    } finally {
      if (alive.current) setSaving(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--spacing-3)', padding: 'var(--spacing-3) 0' }}>
      <div className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
        {t('deviceSettings.zoneDrawHint')}
      </div>

      {/* 拖矩形的預覽框 (16:9)。snapshot 未落地 → 比例底圖 placeholder。 */}
      <div
        ref={surfaceRef}
        role="application"
        aria-label={t('deviceSettings.detectionZone')}
        data-testid="zone-surface"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 9',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-surface-line)',
          background:
            'repeating-linear-gradient(45deg, var(--color-surface-panel2) 0 12px, var(--color-surface-panel) 12px 24px)',
          cursor: disabled ? 'not-allowed' : 'crosshair',
          touchAction: 'none',
          overflow: 'hidden',
          opacity: disabled ? 'var(--opacity-disabled)' : 1,
        }}
      >
        <div
          data-testid="zone-rect"
          style={{
            position: 'absolute',
            left: `${region.x}%`,
            top: `${region.y}%`,
            width: `${region.xlen}%`,
            height: `${region.ylen}%`,
            border: '2px solid var(--color-brand-primary)',
            background: 'color-mix(in srgb, var(--color-brand-primary) 22%, transparent)',
            boxSizing: 'border-box',
            pointerEvents: 'none',
          }}
        />
      </div>

      {/* 讀回狀態 */}
      {load === 'unset' && (
        <p className="text-secondary" role="status" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
          {t('deviceSettings.zoneUnset')}
        </p>
      )}
      {load === 'unavailable' && (
        <p className="text-secondary" role="status" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
          {t('deviceSettings.zoneReadFailed')}
        </p>
      )}
      <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        {t('deviceSettings.zoneCurrent', { x: region.x, y: region.y, w: region.xlen, h: region.ylen })}
      </p>

      {/* 常用區域 preset (8 個) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)' }}>
        {ZONE_PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            className="btn-ghost"
            disabled={disabled}
            style={{ padding: 'var(--spacing-1) var(--spacing-4)', fontSize: 'var(--font-size-sm)' }}
            onClick={() => {
              setRegion(clampRegion(p.region));
              setSaved(false);
            }}
          >
            {t(`deviceSettings.zonePreset_${p.id}`)}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-4)', flexWrap: 'wrap' }}>
        <button className="btn-primary" disabled={disabled || saving} onClick={() => void save()}>
          {saving ? t('common.running') : t('deviceSettings.zoneSave')}
        </button>
        {saved && (
          <span className="text-secondary" role="status" style={{ fontSize: 'var(--font-size-sm)' }}>
            {t('deviceSettings.zoneSaved')}
          </span>
        )}
        {saveError && (
          <span className="error-text" role="alert" style={{ fontSize: 'var(--font-size-sm)' }}>
            {saveError}
          </span>
        )}
      </div>
    </div>
  );
}
